import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { springs } from '../../animation';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { useSoundEffects } from '../../hooks/useSoundEffects';
import { useHaptic } from '../../hooks/useHaptic';
import { useOnboardingStore } from '../../hooks/useOnboardingStore';
import AnimatedButton from '../../components/ui/AnimatedButton';
import MindoraCharacter from '../../components/character/MindoraCharacter';
import SpeechBubble from '../../components/ui/SpeechBubble';
import Confetti from '../../components/effects/Confetti';
import Fireworks from '../../components/effects/Fireworks';
import ScreenShake from '../../components/effects/ScreenShake';
import SparkleCluster from '../../components/decorations/SparkleCluster';
import DecorativeStar from '../../components/decorations/DecorativeStar';
import { INTERESTS } from './interests';

interface Props {
  onFinish: () => void;
}

// Map first interest to a personalized message
function getInterestMessage(interests: string[]): string {
  const firstInterest = interests[0];
  if (!firstInterest) return "We're going to have so much fun learning together!";

  const interest = INTERESTS.find(i => i.id === firstInterest);
  if (!interest) return "We're going to have so much fun learning together!";

  const messages: Record<string, string> = {
    animals: `I know SO many cool animal facts! Did you know octopuses have three hearts?`,
    space: `I love space too! Want to explore the solar system together?`,
    dinosaurs: `Dinosaurs are awesome! I know all about T-Rex and friends!`,
    drawing: `Ooh, an artist! We can learn about colors and famous painters!`,
    music: `Music is magical! Want to learn how sounds work?`,
    sports: `Go team! We can learn about sports science and records!`,
    cooking: `Yummy! Cooking is like science you can eat!`,
    computers: `Beep boop! I'm a computer too! Let's learn to code!`,
    stories: `I love stories! We can read and write amazing adventures!`,
    numbers: `Numbers are like puzzles! Want to solve some with me?`,
    nature: `Nature is beautiful! Trees can actually talk to each other!`,
    history: `History is full of wild stories! Time travel, here we come!`,
  };

  return messages[firstInterest] || "We're going to have so much fun learning together!";
}

export default function ChildMeetBuddyStep({ onFinish }: Props) {
  const { childName, childInterests, resetOnboarding } = useOnboardingStore();
  const { play } = useSoundEffects();
  const haptic = useHaptic();
  const reduced = useReducedMotion();

  const [showBubble, setShowBubble] = useState(false);
  const [showSecondBubble, setShowSecondBubble] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const [celebrating, setCelebrating] = useState(false);

  useEffect(() => {
    // Sound at character entrance
    const soundTimer = setTimeout(() => play('celebratoryWoohoo'), 300);
    // Speech bubble at 1s
    const bubbleTimer = setTimeout(() => setShowBubble(true), 1000);
    // Confetti + Fireworks at 1.5s
    const confettiTimer = setTimeout(() => {
      setShowConfetti(true);
      play('rewardSparkle');
      haptic.celebrate();
    }, 1500);
    // Celebrating mood at 2s
    const celebrateTimer = setTimeout(() => setCelebrating(true), 2000);
    // Second bubble with interest-specific message at 2.5s
    const secondBubbleTimer = setTimeout(() => setShowSecondBubble(true), 2500);
    // Button at 3s
    const buttonTimer = setTimeout(() => setShowButton(true), 3000);

    return () => {
      clearTimeout(soundTimer);
      clearTimeout(bubbleTimer);
      clearTimeout(confettiTimer);
      clearTimeout(celebrateTimer);
      clearTimeout(secondBubbleTimer);
      clearTimeout(buttonTimer);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFinish = () => {
    resetOnboarding();
    onFinish();
  };

  return (
    <ScreenShake trigger={showConfetti}>
      <div className="flex flex-col items-center justify-center min-h-screen px-6 py-12 relative overflow-hidden">
        <Confetti trigger={showConfetti} count={30} duration={3000} />
        <Fireworks trigger={showConfetti} bursts={4} />

        {/* Decorations */}
        <SparkleCluster count={10} spread={120} className="absolute top-12 left-1/2 -translate-x-1/2" />
        <DecorativeStar size={32} color="var(--kid-dandelion)" pulse className="absolute top-20 left-8" />
        <DecorativeStar size={24} color="var(--kid-violet)" pulse className="absolute top-32 right-12" />
        <DecorativeStar size={20} color="var(--kid-berry)" pulse className="absolute bottom-40 left-16" />

        {/* Character entrance */}
        <motion.div
          initial={{ y: 200, opacity: 0, scale: 0.5 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          transition={reduced ? { duration: 0.3, delay: 0.1 } : { ...springs.bouncy, delay: 0.3 }}
        >
          <MindoraCharacter mood={celebrating ? 'celebrating' : 'waving'} size="xl" />
        </motion.div>

        {/* First speech bubble — name highlighted in violet */}
        <AnimatePresence>
          {showBubble && (
            <motion.div
              className="mt-6 max-w-sm w-full"
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={reduced ? { duration: 0.2 } : springs.gentle}
            >
              <SpeechBubble>
                <p className="font-display text-kid-xl text-kid-text text-center">
                  Hi <span className="text-kid-violet">{childName}</span>! I'm Mindora!
                </p>
              </SpeechBubble>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Second speech bubble — personalized to first interest */}
        <AnimatePresence>
          {showSecondBubble && (
            <motion.div
              className="mt-3 max-w-sm w-full"
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={reduced ? { duration: 0.2 } : springs.gentle}
            >
              <SpeechBubble direction="right">
                <p className="text-kid-text font-kid text-kid-sm text-center">
                  {getInterestMessage(childInterests)}
                </p>
              </SpeechBubble>
            </motion.div>
          )}
        </AnimatePresence>

        {/* CTA: "I'm ready!" */}
        <AnimatePresence>
          {showButton && (
            <motion.div
              className="mt-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={reduced ? { duration: 0.2 } : springs.gentle}
            >
              <AnimatedButton
                variant="marshmallow"
                className="btn-marshmallow-meadow"
                onClick={handleFinish}
              >
                I'm ready!
              </AnimatedButton>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </ScreenShake>
  );
}
